import type { HermesProfile, HermesProfileCatalog, SessionSummary } from "@herskin/contracts";
import {
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";

export type ProfileScope = "all" | string;

export function ProfileSwitcher({
  catalog,
  scope,
  sessions,
  defaultDisplayName,
  onSelect,
  onManage,
  openRequest = 0,
}: {
  catalog: HermesProfileCatalog | null;
  scope: ProfileScope;
  sessions: SessionSummary[];
  defaultDisplayName: string;
  onSelect: (scope: ProfileScope) => void;
  onManage: () => void;
  openRequest?: number;
}) {
  const [open, setOpen] = useState(false);
  const profiles = catalog?.items ?? [];
  const selected = profiles.find((item) => item.name === scope) ?? null;
  const runningByProfile = useMemo(() => {
    const counts = new Map<string, number>();
    for (const session of sessions) {
      if (!session.activeRunStatus) continue;
      counts.set(session.profileName, (counts.get(session.profileName) ?? 0) + 1);
    }
    return counts;
  }, [sessions]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (openRequest > 0) setOpen(true);
  }, [openRequest]);

  const displayName = (profile: HermesProfile) =>
    profile.isDefault ? defaultDisplayName : profile.name;
  const currentLabel = scope === "all" ? "全部 Profile" : selected ? displayName(selected) : scope;
  const currentRunning =
    scope === "all"
      ? [...runningByProfile.values()].reduce((sum, value) => sum + value, 0)
      : (runningByProfile.get(scope) ?? 0);

  // 只有 default 时不为移动端会话列表增加额外高度；管理入口仍在“更多”。
  if (profiles.length <= 1) return null;

  return (
    <>
      <button
        type="button"
        className="profile-switcher-trigger"
        aria-label={`切换 Profile，当前：${currentLabel}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={!profiles.length}
        onClick={() => setOpen(true)}
      >
        <span className="profile-switcher-avatar" aria-hidden="true">
          {scope === "all" ? <IconUsers size={18} /> : currentLabel.slice(0, 1).toUpperCase()}
        </span>
        <span className="profile-switcher-copy">
          <strong>{currentLabel}</strong>
          <small>{currentRunning ? `${currentRunning} 个任务运行中` : "Profile 会话范围"}</small>
        </span>
        {profiles.length > 1 ? <IconChevronDown size={17} aria-hidden="true" /> : null}
      </button>

      {open
        ? createPortal(
            <div
              className="profile-switcher-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setOpen(false);
              }}
            >
              <section
                className="profile-switcher-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="profile-switcher-title"
              >
                <header>
                  <div>
                    <span className="section-kicker">会话范围</span>
                    <h2 id="profile-switcher-title">切换 Profile</h2>
                  </div>
                  <button
                    type="button"
                    aria-label="关闭 Profile 选择"
                    onClick={() => setOpen(false)}
                  >
                    <IconX size={20} />
                  </button>
                </header>
                <div className="profile-switcher-options">
                  {profiles.length > 1 ? (
                    <button
                      type="button"
                      className={scope === "all" ? "active" : ""}
                      onClick={() => {
                        onSelect("all");
                        setOpen(false);
                      }}
                    >
                      <span className="profile-switcher-avatar">
                        <IconUsers size={18} />
                      </span>
                      <span>
                        <strong>全部 Profile</strong>
                        <small>聚合查看；新建会话时需先选择接收者</small>
                      </span>
                      {scope === "all" ? <IconCheck size={18} /> : null}
                    </button>
                  ) : null}
                  {profiles.map((item) => {
                    const running = runningByProfile.get(item.name) ?? 0;
                    return (
                      <button
                        type="button"
                        className={scope === item.name ? "active" : ""}
                        key={item.name}
                        onClick={() => {
                          onSelect(item.name);
                          setOpen(false);
                        }}
                      >
                        <span className="profile-switcher-avatar">
                          {displayName(item).slice(0, 1).toUpperCase()}
                        </span>
                        <span>
                          <strong>{displayName(item)}</strong>
                          <small>
                            {item.name}
                            {item.description ? ` · ${item.description}` : ""}
                            {running ? ` · ${running} 个任务运行中` : ""}
                          </small>
                        </span>
                        {scope === item.name ? <IconCheck size={18} /> : null}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="profile-switcher-manage"
                  onClick={() => {
                    setOpen(false);
                    onManage();
                  }}
                >
                  <span>管理 Profile</span>
                  <IconChevronRight size={18} />
                </button>
              </section>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
